import { useEffect, useState } from "react";
import {
  CommunityFormatProfile,
  CommunityCustomFormat,
  CommunityVisualStyleProfile,
  VoiceOfCustomer,
} from "../../../../types/CommunityMediaConfig";
import { getCommunityMediaConfig } from "../../../../database/community/mediaConfig";

// The community's media config (audience voice, format profile, visual style,
// buyer persona) from Community/{id}/CommunityMedia/mediaConfig. The setters
// let the brand editors push their onPersist results back without a refetch.

export type MediaConfigState = {
  loading: boolean;
  voc?: VoiceOfCustomer;
  formatProfile?: CommunityFormatProfile;
  customFormats?: CommunityCustomFormat[];
  visualStyleProfile?: CommunityVisualStyleProfile;
  hasBuyerPersona: boolean;
  /** Persona or at least one audience quote saved. Gates research features. */
  hasAudienceData: boolean;
  setVoc: (next?: VoiceOfCustomer) => void;
  setFormatProfile: (next?: CommunityFormatProfile) => void;
  addCustomFormats: (additions: CommunityCustomFormat[]) => void;
  setVisualStyleProfile: (next?: CommunityVisualStyleProfile) => void;
};

export const useMediaConfig = (communityId?: string): MediaConfigState => {
  const [loading, setLoading] = useState(false);
  const [voc, setVoc] = useState<VoiceOfCustomer | undefined>(undefined);
  const [formatProfile, setFormatProfile] = useState<
    CommunityFormatProfile | undefined
  >(undefined);
  const [customFormats, setCustomFormats] = useState<
    CommunityCustomFormat[] | undefined
  >(undefined);
  const [visualStyleProfile, setVisualStyleProfile] = useState<
    CommunityVisualStyleProfile | undefined
  >(undefined);
  const [hasBuyerPersona, setHasBuyerPersona] = useState<boolean>(false);

  useEffect(() => {
    if (!communityId) {
      setVoc(undefined);
      setFormatProfile(undefined);
      setCustomFormats(undefined);
      setVisualStyleProfile(undefined);
      setHasBuyerPersona(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getCommunityMediaConfig(communityId)
      .then((config) => {
        if (cancelled) return;
        setVoc(config.voiceOfCustomer);
        setFormatProfile(config.formatProfile);
        setCustomFormats(config.customFormats);
        setVisualStyleProfile(config.visualStyleProfile);
        setHasBuyerPersona(Boolean(config.buyerPersona?.trim()));
      })
      .catch((err) => {
        console.error("Error loading media config:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [communityId]);

  const hasAudienceData =
    hasBuyerPersona || Boolean(voc?.quotes && voc.quotes.length > 0);

  const addCustomFormats = (additions: CommunityCustomFormat[]) => {
    if (!additions.length) return;
    setCustomFormats((prev) => [...(prev || []), ...additions]);
  };

  return {
    loading,
    voc,
    formatProfile,
    customFormats,
    visualStyleProfile,
    hasBuyerPersona,
    hasAudienceData,
    setVoc,
    setFormatProfile,
    addCustomFormats,
    setVisualStyleProfile,
  };
};

export default useMediaConfig;
